import * as THREE from 'three';

class Door {
  constructor(wall, positionOnWall = 0.5, width = 0.9, height = 2.1, doorType = 'swing') {
    this.id = this.generateId();
    this.wall = wall;
    this.positionOnWall = positionOnWall;
    this.width = width;
    this.height = height;
    this.doorType = doorType; // 'swing' | 'sliding' | 'double'
    this.mesh = null;
    this.frameMesh = null;
    this.doorPanelMesh = null; // Pannello primario (swing / sliding) o panello sinistro (double)
    this.openAngle = 0;
    this.isOpen = false;
    this.isSelected = false;
    this.isFlipped = false;

    // Battente
    this.doorPivot = null;
    this.handleFront = null;
    this.handleBack = null;

    // Doppia
    this.leftPivot = null;
    this.rightPivot = null;
    this.leftPanelMesh = null;
    this.rightPanelMesh = null;
    this.leftOpenAngle = 0;
    this.rightOpenAngle = 0;

    // Sezionale
    this.sectionalPivot = null; // trasla lungo i binari (asse Z)
    this.sectionalAngle = 0; // angolo assoluto del pannello 0 (0 = chiuso, -π/2 = aperto)
    this.sectionalSlideZ = 0; // scorrimento lungo i binari (0 = chiuso, -height/2 = aperto)
    this.sectionalHinges = []; // [Group] una cerniera locale per pannello, in cascata
    this._sectionalZOffset = 0; // pannello centrato nel telaio
  }

  generateId() {
    return 'door_' + Math.random().toString(36).substr(2, 9);
  }

  getWorldPosition() {
    return new THREE.Vector3().lerpVectors(
      this.wall.start,
      this.wall.end,
      this.positionOnWall
    );
  }

  getRotation() {
    const baseRotation = this.wall.getAngle();
    return this.isFlipped ? baseRotation + Math.PI : baseRotation;
  }

  flip() {
    if (this.doorType === 'sliding') {
      // Sposta positionOnWall di una larghezza nella direzione del flip
      const shift = this.isFlipped ? -this.width / this.wall.getLength() : this.width / this.wall.getLength();
      this.positionOnWall += shift;
      this.isFlipped = !this.isFlipped;
      this.updatePosition();
      this.wall.rebuildWallSegments?.();
      console.log('Porta scorrevole ruotata e riposizionata:', this.positionOnWall, this.isFlipped ? 'flipped' : 'normale');
      return;
    }

    this.isFlipped = !this.isFlipped;
    this.updatePosition();
    console.log('Porta ruotata di 180°:', this.isFlipped ? 'girata' : 'normale');
  }

  createMesh() {
    this.mesh = new THREE.Group();

    switch (this.doorType) {
      case 'sliding': this._buildSlidingDoor();    break;
      case 'double': this._buildDoubleDoor();     break;
      case 'sectional': this._buildSectionalDoor();  break;
      default: this._buildSwingDoor();      break;
    }

    const position = this.getWorldPosition();
    this.mesh.position.copy(position);
    this.mesh.position.y = 0;
    this.mesh.rotation.y = this.getRotation();
    this.mesh.userData.door = this;
    return this.mesh;
  }

  // PORTA BATTENTE
  _buildSwingDoor() {
    const frameMat = new THREE.MeshStandardMaterial({ color: 0x8b4513, roughness: 0.7, metalness: 0.1 });
    const panelMat = new THREE.MeshStandardMaterial({ color: 0xd4a574, roughness: 0.6, metalness: 0.2 });
    const handleMat = new THREE.MeshStandardMaterial({ color: 0xaaaaaa, roughness: 0.3, metalness: 0.8 });

    const ft = 0.08;  // spessore montante
    const fd = this.wall.thickness + 0.04;
    const pt = 0.04;  // spessore pannello

    // Frame
    const jamb = new THREE.BoxGeometry(ft, this.height, fd);

    const leftJamb = new THREE.Mesh(jamb, frameMat);
    leftJamb.position.set(-this.width / 2 - ft / 2, this.height / 2, 0);
    leftJamb.castShadow = false;
    leftJamb.receiveShadow = false;

    const rightJamb = new THREE.Mesh(jamb, frameMat);
    rightJamb.position.set(this.width / 2 + ft / 2, this.height / 2, 0);
    rightJamb.castShadow = false;
    rightJamb.receiveShadow = false;

    const headerGeometry = new THREE.BoxGeometry(this.width + ft * 2, ft, fd);
    const header = new THREE.Mesh(headerGeometry, frameMat);
    header.position.set(0, this.height + ft / 2, 0);
    header.castShadow = false;
    header.receiveShadow = false;

    this.frameMesh = new THREE.Group();
    this.frameMesh.add(leftJamb, rightJamb, header);
    this.mesh.add(this.frameMesh);

    // Pannello
    const panelGeometry = new THREE.BoxGeometry(this.width, this.height, pt);
    this.doorPanelMesh = new THREE.Mesh(panelGeometry, panelMat);
    this.doorPanelMesh.castShadow = false;
    this.doorPanelMesh.receiveShadow = false;
    this.doorPanelMesh.position.set(this.width / 2, this.height / 2, 0);

    this.doorPivot = new THREE.Group();
    this.doorPivot.position.set(-this.width / 2, 0, 0);
    this.doorPivot.add(this.doorPanelMesh);

    // Maniglie
    const handleGeometry = new THREE.CylinderGeometry(0.012, 0.012, 0.1, 8);

    this.handleFront = new THREE.Mesh(handleGeometry, handleMat);
    this.handleFront.rotation.x = Math.PI / 2;
    this.handleFront.rotation.z = Math.PI / 2;
    this.handleFront.geometry.translate(0, 0.05, 0);
    this.handleFront.position.set(this.width / 2 - 0.1, this.height / 2 - 1, pt / 2 + 0.05);
    this.handleFront.castShadow = false;
    this.doorPanelMesh.add(this.handleFront);

    this.handleBack = new THREE.Mesh(handleGeometry.clone(), handleMat);
    this.handleBack.rotation.x = Math.PI / 2;
    this.handleBack.rotation.z = Math.PI / 2;
    this.handleBack.geometry.translate(0, 0.05, 0);
    this.handleBack.position.set(this.width / 2 - 0.1, this.height / 2 - 1, -pt / 2 - 0.05);
    this.handleBack.castShadow = false;
    this.doorPanelMesh.add(this.handleBack);

    this.handle = this.handleFront;
    this.mesh.add(this.doorPivot);
  }

  // PORTA SCORREVOLE
  _buildSlidingDoor() {
    const frameMat = new THREE.MeshStandardMaterial({ color: 0x8b4513, roughness: 0.7, metalness: 0.1 });
    const panelMat = new THREE.MeshStandardMaterial({ color: 0xd4a574, roughness: 0.6, metalness: 0.2 });
    const handleMat = new THREE.MeshStandardMaterial({ color: 0xaaaaaa, roughness: 0.3, metalness: 0.8 });

    const ft = 0.08; // spessore montante
    const fd = this.wall.thickness + 0.04;
    const pt = 0.04; // spessore pannello

    // Telaio
    this.frameMesh = new THREE.Group();

    // Montante sinistro
    const lj = new THREE.Mesh(new THREE.BoxGeometry(ft, this.height, fd), frameMat);
    lj.position.set(-this.width / 2 - ft / 2, this.height / 2, 0);
    lj.castShadow = false;
    lj.receiveShadow = false;
    this.frameMesh.add(lj);

    // Montante destro
    const rj = new THREE.Mesh(new THREE.BoxGeometry(ft, this.height, fd), frameMat);
    rj.position.set(this.width / 2 + ft / 2, this.height / 2, 0);
    rj.castShadow = false;
    rj.receiveShadow = false;
    this.frameMesh.add(rj);

    // Architrave
    const hdr = new THREE.Mesh(new THREE.BoxGeometry(this.width + ft * 2, ft, fd), frameMat);
    hdr.position.set(0, this.height + ft / 2, 0);
    hdr.castShadow = false;
    this.frameMesh.add(hdr);

    this.mesh.add(this.frameMesh);

    // Pannello scorrevole
    const panelGeometry = new THREE.BoxGeometry(this.width, this.height, pt);
    this.doorPanelMesh = new THREE.Mesh(panelGeometry, panelMat);
    this.doorPanelMesh.castShadow = false;
    this.doorPanelMesh.receiveShadow = false;
    this.doorPanelMesh.position.set(0, this.height / 2, 0);

    // Maniglie cilindriche
    const handleGeometry = new THREE.CylinderGeometry(0.012, 0.012, 0.1, 8);

    this.handleFront = new THREE.Mesh(handleGeometry, handleMat);
    this.handleFront.geometry.translate(0, 0.05, 0);
    this.handleFront.position.set(-this.width / 2 + 0.1, 1.0 - this.height / 2, pt / 2 + 0.05);
    this.handleFront.castShadow = false;
    this.doorPanelMesh.add(this.handleFront);

    this.handleBack = new THREE.Mesh(handleGeometry.clone(), handleMat);
    this.handleBack.geometry.translate(0, 0.05, 0);
    this.handleBack.position.set(-this.width / 2 + 0.1, 1.0 - this.height / 2, -pt / 2 - 0.05);
    this.handleBack.castShadow = false;
    this.doorPanelMesh.add(this.handleBack);

    this.mesh.add(this.doorPanelMesh);
  }

  // PORTA DOPPIA ANTA
  _buildDoubleDoor() {
    const frameMat  = new THREE.MeshStandardMaterial({ color: 0x8b4513, roughness: 0.7, metalness: 0.1 });
    const panelMat  = new THREE.MeshStandardMaterial({ color: 0xd4a574, roughness: 0.6, metalness: 0.2 });
    const handleMat = new THREE.MeshStandardMaterial({ color: 0xaaaaaa, roughness: 0.3, metalness: 0.8 });

    const ft = 0.08; // spessore montante
    const fd = this.wall.thickness + 0.04;
    const pt = 0.04; // spessore pannello
    const hw = this.width / 2; // metà della larghezza totale
    const pw = hw; // larghezza di ogni pannello

    // Frame
    this.frameMesh = new THREE.Group();

    const addFrameBox = (w, h, d, x, y) => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), frameMat);
      m.position.set(x, y, 0);
      m.castShadow = false;
      m.receiveShadow = false;
      this.frameMesh.add(m);
    };

    addFrameBox(ft, this.height, fd, -hw - ft / 2, this.height / 2); // montante sinistro
    addFrameBox(ft, this.height, fd,  hw + ft / 2, this.height / 2); // montante destro
    addFrameBox(this.width + ft * 2, ft, fd, 0, this.height + ft / 2); // architrave

    this.mesh.add(this.frameMesh);

    // Pannello sinistro
    const leftPanelGeo = new THREE.BoxGeometry(pw, this.height, pt);
    this.leftPanelMesh = new THREE.Mesh(leftPanelGeo, panelMat);
    this.leftPanelMesh.castShadow = false;
    this.leftPanelMesh.receiveShadow = false;
    // Offset in modo che il bordo sinistro del pannello si trovi in x=0 nel leftPivot
    this.leftPanelMesh.position.set(pw / 2, this.height / 2, 0);

    this._addDoubleHandle(this.leftPanelMesh,  handleMat, pt,  pw / 2 - 0.1);

    this.leftPivot = new THREE.Group();
    this.leftPivot.position.set(-hw, 0, 0);
    this.leftPivot.add(this.leftPanelMesh);
    this.mesh.add(this.leftPivot);

    // Pannello destro
    const rightPanelGeo = new THREE.BoxGeometry(pw, this.height, pt);
    this.rightPanelMesh = new THREE.Mesh(rightPanelGeo, panelMat);
    this.rightPanelMesh.castShadow = false;
    this.rightPanelMesh.receiveShadow = false;
    // Offset in modo che il bordo destro del pannello si trovi in x=0 nel rightPivot
    this.rightPanelMesh.position.set(-pw / 2, this.height / 2, 0);

    this._addDoubleHandle(this.rightPanelMesh, handleMat, pt, -pw / 2 + 0.1, true);

    this.rightPivot = new THREE.Group();
    this.rightPivot.position.set(hw, 0, 0);
    this.rightPivot.add(this.rightPanelMesh);
    this.mesh.add(this.rightPivot);

    // Riferimento di compatibilità utilizzato dai controlli di selezione/deselezione e di collisione
    this.doorPanelMesh = this.leftPanelMesh;
  }

  // PORTONE SEZIONALE MOTORIZZATO
  _buildSectionalDoor() {
    const frameMat = new THREE.MeshStandardMaterial({ color: 0x8b4513, roughness: 0.7,  metalness: 0.1  });
    const panelMat = new THREE.MeshStandardMaterial({ color: 0x8b4513, roughness: 0.55, metalness: 0.12 });
    const darkMetal = new THREE.MeshStandardMaterial({ color: 0x555555, roughness: 0.4,  metalness: 0.8  });
    const railMat = new THREE.MeshStandardMaterial({ color: 0x333333, roughness: 0.35, metalness: 0.9  });

    const jambWidth = 0.08; // spessore montante telaio
    const frameDepth = this.wall.thickness + 0.04; // profondità telaio (attraversa il muro)
    const panelThickness = 0.048; // spessore pannelli
    const w = this.width;
    const h = this.height;

    // Il portone è posizionato sul lato interno del muro
    const zOffset = -(this.wall.thickness / 2 + panelThickness / 2);
    this._sectionalZOffset = zOffset;

    // Telaio
    const addJamb = (x) => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(jambWidth, h, frameDepth), frameMat);
      m.position.set(x, h / 2, 0);
      m.castShadow = false;
      m.receiveShadow = false;
      return m;
    };
    this.frameMesh = new THREE.Group();
    this.frameMesh.add(
      addJamb(-w / 2 - jambWidth / 2),
      addJamb( w / 2 + jambWidth / 2)
    );

    // Architrave
    const header = new THREE.Mesh(
      new THREE.BoxGeometry(w + jambWidth * 2, jambWidth, frameDepth), frameMat
    );
    header.position.set(0, h + jambWidth / 2, 0);
    header.castShadow = false;
    this.frameMesh.add(header);
    this.mesh.add(this.frameMesh);

    // BINARI LATERALI
    const railArcRadius = 0.50; // raggio dell'arco di raccordo (m)
    const railHorizLen = h - railArcRadius; // lunghezza tratto orizzontale
    const railVertHeight = h * 0.765; // altezza tratto verticale

    const buildRailCurve = (railX) => {
      const pts = [];

      // Tratto 1 — verticale (da y=0 a y=railVertHeight)
      const vertSteps = 8;
      for (let i = 0; i <= vertSteps; i++) {
        pts.push(new THREE.Vector3(railX, (railVertHeight / vertSteps) * i, zOffset));
      }

      // Tratto 2 — arco 90°
      const arcSteps = 24;
      for (let i = 1; i <= arcSteps; i++) {
        const theta = (Math.PI / 2) * (i / arcSteps);
        pts.push(new THREE.Vector3(
          railX,
          railVertHeight + railArcRadius * Math.sin(theta),
          zOffset - railArcRadius * (1 - Math.cos(theta))
        ));
      }

      // Tratto 3 — orizzontale (a quota railVertHeight + railArcRadius)
      const yHorizRail = railVertHeight + railArcRadius;
      const zArcEnd = zOffset - railArcRadius;
      const horizSteps = 12;
      for (let i = 1; i <= horizSteps; i++) {
        pts.push(new THREE.Vector3(railX, yHorizRail, zArcEnd - (railHorizLen / horizSteps) * i));
      }

      return new THREE.CatmullRomCurve3(pts, false, 'catmullrom', 0.0);
    };

    [-w / 2, w / 2].forEach(railX => {
      const curve = buildRailCurve(railX);

      // Sezione rettangolare del binario: larghezza = spessore pannello, profondità piatta
      const railShape = new THREE.Shape();
      railShape.moveTo(-panelThickness / 2, -0.005);
      railShape.lineTo( panelThickness / 2, -0.005);
      railShape.lineTo( panelThickness / 2,  0.005);
      railShape.lineTo(-panelThickness / 2,  0.005);
      railShape.closePath();

      const railGeo = new THREE.ExtrudeGeometry(railShape, {
        steps: 80,
        bevelEnabled: false,
        extrudePath: curve
      });
      const rail = new THREE.Mesh(railGeo, railMat);
      rail.castShadow = false;
      rail.receiveShadow = false;
      this.mesh.add(rail);
    });

    // CARRELLO / PIVOT PANNELLI
    // Pivot in sommità all'apertura; la sua Z varia durante l'animazione
    this.sectionalPivot = new THREE.Group();
    this.sectionalPivot.position.set(0, h, zOffset);
    this.mesh.add(this.sectionalPivot);

    // CATENA DI PANNELLI INCERNIERATI
    const numPanels = 4;
    const panelH = h / numPanels;
    const grooveH = 0.008; // altezza scanalatura / cerniera visibile

    this.sectionalHinges = [];
    let parentGroup = this.sectionalPivot;

    for (let i = 0; i < numPanels; i++) {
      const hingeGroup = new THREE.Group();
      hingeGroup.position.set(0, i === 0 ? 0 : -panelH, 0);

      const panel = new THREE.Mesh(
        new THREE.BoxGeometry(w, panelH - grooveH, panelThickness), panelMat
      );
      panel.position.set(0, -panelH / 2, 0);
      panel.castShadow = false;
      panel.receiveShadow = false;
      hingeGroup.add(panel);

      // Scanalatura visibile al bordo inferiore del pannello
      const groove = new THREE.Mesh(
        new THREE.BoxGeometry(w, grooveH * 2, panelThickness + 0.002), darkMetal
      );
      groove.position.set(0, -panelH + grooveH, 0.001);
      hingeGroup.add(groove);

      parentGroup.add(hingeGroup);
      this.sectionalHinges.push(hingeGroup);
      parentGroup = hingeGroup; // il pannello successivo è figlio di questo
    }

    // STAFFA CENTRALE
    const bracketY = h + 0.2; // altezza staffa (tratto orizzontale)
    const bracketH = 0.04 // spessore staffa
    const bracketLength = h + 0.5; // lunghezza della staffa
    const bracketZCenter = zOffset - bracketLength / 2;

    const bracketMesh = new THREE.Mesh(
      new THREE.BoxGeometry(0.06, bracketH, bracketLength), railMat
    );
    bracketMesh.position.set(0, bracketY, bracketZCenter);
    bracketMesh.castShadow = false;
    bracketMesh.receiveShadow = false;
    this.mesh.add(bracketMesh);

    // MOTORE
    const motorW = 0.30, motorH = 0.18, motorD = 0.22;
    const motorMat = new THREE.MeshStandardMaterial({ color: 0x2a2a2a, roughness: 0.5, metalness: 0.75 });
    const motorMesh = new THREE.Mesh(new THREE.BoxGeometry(motorW, motorH, motorD), motorMat);

    const motorY = bracketY - bracketH / 2 - motorH / 2; // appoggiato sotto la staffa
    const motorZ = bracketZCenter - bracketLength / 2 + motorD / 2; // allineato all'estremità libera della staffa

    motorMesh.position.set(0, motorY, motorZ);
    motorMesh.castShadow = false;
    this.mesh.add(motorMesh);

    // Punto di ancoraggio fune: faccia del motore rivolta verso il portone (+Z)
    this._motorCableAnchor = new THREE.Vector3(0, motorY, motorZ + motorD / 2);

    // FUNE METALLICA
    const cableRadius = 0.005; // spessore
    const cableSegments = 8; // sezioni radiali del tubo

    const initCableCurve = new THREE.LineCurve3(
      this._motorCableAnchor.clone(),
      new THREE.Vector3(0, h, zOffset)
    );
    const cableGeo = new THREE.TubeGeometry(initCableCurve, 1, cableRadius, cableSegments, false);
    this._cableMesh = new THREE.Mesh(cableGeo, new THREE.MeshStandardMaterial({ color: 0xbbbbbb, metalness: 0.8, roughness: 0.3 }));
    this.mesh.add(this._cableMesh);

    // Riferimento compatibilità
    this.doorPanelMesh = this.sectionalHinges[0];
  }

  // Aggiorna la geometria della fune collegando il motore alla sommità del primo pannello
  _updateCable() {
    if (!this._cableMesh || !this.sectionalPivot) return;

    const anchor = this._motorCableAnchor;
    const tip = new THREE.Vector3(0, this.height, this.sectionalPivot.position.z);

    const newCurve = new THREE.LineCurve3(anchor, tip);
    this._cableMesh.geometry.dispose();
    this._cableMesh.geometry = new THREE.TubeGeometry(newCurve, 1, 0.005, 8, false);  // stesso valore di cableRadius
  }

  _addDoubleHandle(panelMesh, handleMat, pt, localX, mirrored = false) {
    const localY = 1.0 - this.height / 2;
    const leverDir = mirrored ? -0.05 : 0.05;

    const hGeo = new THREE.CylinderGeometry(0.012, 0.012, 0.1, 8);

    const hFront = new THREE.Mesh(hGeo, handleMat);
    hFront.rotation.x = Math.PI / 2;
    hFront.rotation.z = Math.PI / 2;
    hFront.geometry.translate(0, leverDir, 0);
    hFront.position.set(localX, localY, pt / 2 + 0.05);
    hFront.castShadow = false;
    panelMesh.add(hFront);

    const hBack = new THREE.Mesh(hGeo.clone(), handleMat);
    hBack.rotation.x = Math.PI / 2;
    hBack.rotation.z = Math.PI / 2;
    hBack.geometry.translate(0, leverDir, 0);
    hBack.position.set(localX, localY, -pt / 2 - 0.05);
    hBack.castShadow = false;
    panelMesh.add(hBack);
  }

  // ANIMAZIONI
  animateHandle(duration, callback) {
    if (!this.handleFront || !this.handleBack) {
      if (callback) callback();
      return;
    }

    const startTime = performance.now();
    const downAngle = Math.PI / 4;

    const animate = (currentTime) => {
      const elapsed  = (currentTime - startTime) / 1000;
      const progress = Math.min(elapsed / duration, 1);

      if (progress < 0.5) {
        const angle = (Math.PI / 4) * (progress * 2);
        this.handleFront.rotation.y = angle;
        this.handleBack.rotation.y  = angle;
      } else {
        const angle = downAngle * (1 - (progress - 0.5) * 2);
        this.handleFront.rotation.y = angle;
        this.handleBack.rotation.y  = angle;
      }

      window.requestRender?.();

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.handleFront.rotation.y = 0;
        this.handleBack.rotation.y  = 0;
        if (callback) callback();
      }
    };

    requestAnimationFrame(animate);
  }

  open(duration) {
    if (this.isOpen) return;

    switch (this.doorType) {
      case 'sliding':   this._openSliding(duration ?? 1.0);   break;
      case 'double':    this._openDouble(duration ?? 1.0);    break;
      case 'sectional': this._openSectional(duration ?? 3.0); break;
      default:          this._openSwing(duration ?? 1.0);     break;
    }
  }

  close(duration) {
    if (!this.isOpen) return;

    switch (this.doorType) {
      case 'sliding':   this._closeSliding(duration ?? 1.0);   break;
      case 'double':    this._closeDouble(duration ?? 1.0);    break;
      case 'sectional': this._closeSectional(duration ?? 3.0); break;
      default:          this._closeSwing(duration ?? 1.0);     break;
    }
  }

  toggle() {
    if (this.isOpen) this.close();
    else             this.open();
  }

  // Battente
  _openSwing(duration) {
    this.isOpen = true;
    this.animateHandle(0.4, () => {
      const targetAngle = -Math.PI / 2;
      const startTime   = performance.now();
      const animate = (now) => {
        const p = Math.min((now - startTime) / 1000 / duration, 1);
        const e = 1 - Math.pow(1 - p, 3);
        this.openAngle = e * targetAngle;
        this.doorPivot.rotation.y = this.openAngle;
        window.requestRender?.();
        if (p < 1) requestAnimationFrame(animate);
      };
      requestAnimationFrame(animate);
    });
  }

  _closeSwing(duration) {
    this.isOpen = false;
    const startTime = performance.now();
    const animate = (now) => {
      const p = Math.min((now - startTime) / 1000 / duration, 1);
      const e = p * p * p;
      this.openAngle = -Math.PI / 2 * (1 - e);
      this.doorPivot.rotation.y = this.openAngle;
      window.requestRender?.();
      if (p < 1) {
        requestAnimationFrame(animate);
      } else {
        this.openAngle = 0;
        this.doorPivot.rotation.y = 0;
      }
    };
    requestAnimationFrame(animate);
  }

  // Scorrevole
  _openSliding(duration) {
    this.isOpen = true;
    const startTime = performance.now();
    const targetX = this.width;
    const animate = (now) => {
      const p = Math.min((now - startTime) / 1000 / duration, 1);
      const e = 1 - Math.pow(1 - p, 3);
      this.doorPanelMesh.position.x = targetX * e;
      window.requestRender?.();
      if (p < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }

  _closeSliding(duration) {
    this.isOpen = false;
    const startTime = performance.now();
    const startX = this.width;
    const animate = (now) => {
      const p = Math.min((now - startTime) / 1000 / duration, 1);
      const e = p * p * p;
      this.doorPanelMesh.position.x = startX * (1 - e);
      window.requestRender?.();
      if (p < 1) {
        requestAnimationFrame(animate);
      } else {
        this.doorPanelMesh.position.x = 0;
      }
    };
    requestAnimationFrame(animate);
  }

  // Doppia
  _openDouble(duration) {
    this.isOpen = true;
    const targetAngle = -Math.PI / 2;
    const startTime = performance.now();
    const animate = (now) => {
      const p = Math.min((now - startTime) / 1000 / duration, 1);
      const e = 1 - Math.pow(1 - p, 3);
      this.leftOpenAngle  = e * targetAngle;
      this.rightOpenAngle = -e * targetAngle;
      this.leftPivot.rotation.y = this.leftOpenAngle;
      this.rightPivot.rotation.y = this.rightOpenAngle;
      window.requestRender?.();
      if (p < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }

  _closeDouble(duration) {
    this.isOpen = false;
    const startLeft = this.leftOpenAngle;
    const startRight = this.rightOpenAngle;
    const startTime = performance.now();
    const animate = (now) => {
      const p = Math.min((now - startTime) / 1000 / duration, 1);
      const e = 1 - Math.pow(1 - p, 3);
      this.leftOpenAngle  = startLeft  * (1 - e);
      this.rightOpenAngle = startRight * (1 - e);
      this.leftPivot.rotation.y  = this.leftOpenAngle;
      this.rightPivot.rotation.y = this.rightOpenAngle;
      window.requestRender?.();
      if (p < 1) {
        requestAnimationFrame(animate);
      } else {
        this.leftOpenAngle = this.rightOpenAngle = 0;
        this.leftPivot.rotation.y  = 0;
        this.rightPivot.rotation.y = 0;
      }
    };
    requestAnimationFrame(animate);
  }

  // Portone sezionale
  _openSectional(duration = 3.0) {
    if (!this.sectionalPivot || !this.sectionalHinges.length) return;
    this.isOpen = true;

    const numPanels = this.sectionalHinges.length;
    const panelH = this.height / numPanels;
    const targetAngle = -Math.PI / 2;
    const startTime = performance.now();
    const lag = 0.10;

    const animate = (now) => {
      const p = Math.min((now - startTime) / 1000 / duration, 1);

      const absAngles = this.sectionalHinges.map((_, i) => {
        const localP = Math.max(0, Math.min(1, (p - i * lag) / (1 - (numPanels - 1) * lag)));
        const localE = 1 - Math.pow(1 - localP, 3);
        return targetAngle * localE;
      });

      this.sectionalHinges.forEach((hinge, i) => {
        const parentAbs = i === 0 ? 0 : absAngles[i - 1];
        hinge.rotation.x = absAngles[i] - parentAbs;
      });
      this.sectionalAngle = absAngles[0];

      this.sectionalSlideZ = panelH * absAngles.reduce((sum, a) => sum + Math.sin(a), 0);
      this.sectionalPivot.position.z = this._sectionalZOffset + this.sectionalSlideZ;

      this._updateCable();
      window.requestRender?.();
      if (p < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }

  _closeSectional(duration = 3.0) {
    if (!this.sectionalPivot || !this.sectionalHinges.length) return;
    this.isOpen = false;

    const numPanels = this.sectionalHinges.length;
    const panelH = this.height / numPanels;
    const startTime = performance.now();
    const lag = 0.10;

    const animate = (now) => {
      const p = Math.min((now - startTime) / 1000 / duration, 1);

      const absAngles = this.sectionalHinges.map((_, i) => {
        const reversedI = numPanels - 1 - i;
        const localP = Math.max(0, Math.min(1, (p - reversedI * lag) / (1 - (numPanels - 1) * lag)));
        const localE = localP * localP * localP;
        return -Math.PI / 2 * (1 - localE);
      });

      this.sectionalHinges.forEach((hinge, i) => {
        const parentAbs = i === 0 ? 0 : absAngles[i - 1];
        hinge.rotation.x = absAngles[i] - parentAbs;
      });
      this.sectionalAngle = absAngles[0];

      this.sectionalSlideZ = panelH * absAngles.reduce((sum, a) => sum + Math.sin(a), 0);
      this.sectionalPivot.position.z = this._sectionalZOffset + this.sectionalSlideZ;

      this._updateCable();
      window.requestRender?.();
      if (p < 1) {
        requestAnimationFrame(animate);
      } else {
        this.sectionalAngle = 0;
        this.sectionalSlideZ = 0;
        this.sectionalPivot.position.z = this._sectionalZOffset;
        this.sectionalHinges.forEach(h => { h.rotation.x = 0; });
        this._updateCable();
      }
    };
    requestAnimationFrame(animate);
  }

  // SELEZIONE
  select() {
    this.isSelected = true;
    if (this.mesh) {
      this.mesh.traverse(child => {
        if (child.material && child.material.emissive) {
          child.material.emissive.setHex(0xddeeff);
        }
      });
    }
    window.requestRender?.();
  }

  deselect() {
    this.isSelected = false;
    if (this.mesh) {
      this.mesh.traverse(child => {
        if (child.material && child.material.emissive) {
          child.material.emissive.setHex(0x000000);
        }
      });
    }
    window.requestRender?.();
  }

  // AGGIORNAMENTO POSIZIONE
  updatePosition() {
    if (!this.mesh) return;
    const position = this.getWorldPosition();
    this.mesh.position.copy(position);
    this.mesh.position.y = 0;
    this.mesh.rotation.y = this.getRotation();
    window.requestRender?.();
  }

  // BOUNDING BOX
  getBoundingBox() {
    return {
      start: this.positionOnWall - this.width / 2 / this.wall.getLength(),
      end: this.positionOnWall + this.width / 2 / this.wall.getLength(),
      width: this.width
    };
  }

  // OCCUPANCY BOX
  getOccupancyBox() {
    if (this.doorType === 'sliding') {
      const slideDX = this.width;
      const wallLen = this.wall.getLength();
      if (this.isFlipped) {
        return {
          start: this.positionOnWall - (this.width / 2 + slideDX) / wallLen,
          end: this.positionOnWall + this.width / 2 / wallLen,
          width: this.width + slideDX
        };
      }
      return {
        start: this.positionOnWall - this.width / 2 / wallLen,
        end: this.positionOnWall + (this.width / 2 + slideDX) / wallLen,
        width: this.width + slideDX
      };
    }
    return this.getBoundingBox();
  }

  // DISPOSE
  dispose() {
    if (this.mesh) {
      this.mesh.traverse((child) => {
        if (child.geometry) child.geometry.dispose();
        if (child.material) {
          const mats = Array.isArray(child.material) ? child.material : [child.material];
          mats.forEach(m => m.dispose());
        }
      });
    }
  }
}

export default Door;